import React, { useState, useEffect } from 'react';
import { MessageSquare, Mic, Phone, Settings, Check, Loader2, Volume2, ToggleLeft, ToggleRight, PenLine } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

const VOICE_OPTIONS = [
  { value: 'nova', label: 'Nova (energisch)', description: 'Energetisch, freundlich' },
  { value: 'alloy', label: 'Alloy (neutral)', description: 'Neutral, ausgewogen' },
  { value: 'shimmer', label: 'Shimmer (hell)', description: 'Hell, freundlich' },
  { value: 'echo', label: 'Echo (ruhig)', description: 'Ruhig, sanft' },
  { value: 'fable', label: 'Fable (expressiv)', description: 'Expressiv, erzählend' },
  { value: 'onyx', label: 'Onyx (tief)', description: 'Tief, autoritär' },
];

export default function AdminChatMode() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/chat-mode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Einstellungen');
      // Set defaults if not found
      setSettings({
        active_mode: 'text',
        voice_settings: {
          tts_voice: 'nova',
          tts_speed: 1.0,
          language: 'de'
        },
        phone_settings: {
          twilio_phone_number: '',
          greeting_message: 'Willkommen bei Factory-Hype! Wie kann ich dir helfen?',
          is_configured: false
        },
        show_mode_choice: false,
        mode_choice_message: `Hey 👋 ich bin Lea von Factory-Hype.
Schön, dass du da bist 😊

Du kannst hier ganz entspannt schreiben oder sprechen –
kein Anruf, kein Telefonat, nur eine lockere Unterhaltung im Chat.

Was ist dir lieber?`,
        appointment_mode: 'with_appointment'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (mode) => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/chat-mode`, {
        active_mode: mode,
        voice_settings: settings.voice_settings,
        phone_settings: settings.phone_settings,
        show_mode_choice: settings.show_mode_choice,
        mode_choice_message: settings.mode_choice_message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings(prev => ({ ...prev, active_mode: mode }));
      setSuccess(`Chat-Modus auf "${mode === 'text' ? 'Text-Chat' : mode === 'voice' ? 'Voice-Chat' : 'Telefon'}" geändert!`);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceSettingsChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      voice_settings: {
        ...prev.voice_settings,
        [key]: value
      }
    }));
  };

  const handlePhoneSettingsChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      phone_settings: {
        ...prev.phone_settings,
        [key]: value
      }
    }));
  };

  const handleModeChoiceToggle = (enabled) => {
    setSettings(prev => ({
      ...prev,
      show_mode_choice: enabled
    }));
  };

  const handleModeChoiceMessageChange = (message) => {
    setSettings(prev => ({
      ...prev,
      mode_choice_message: message
    }));
  };

  const handleSaveAppointmentMode = async (mode) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/chat-mode`, {
        active_mode: settings.active_mode,
        voice_settings: settings.voice_settings,
        phone_settings: settings.phone_settings,
        show_mode_choice: settings.show_mode_choice,
        mode_choice_message: settings.mode_choice_message,
        appointment_mode: mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(mode === 'contact_only' ? 'Nur Kontakt-Modus aktiviert!' : 'Termin-Modus aktiviert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/chat-mode`, {
        active_mode: settings.active_mode,
        voice_settings: settings.voice_settings,
        phone_settings: settings.phone_settings,
        show_mode_choice: settings.show_mode_choice,
        mode_choice_message: settings.mode_choice_message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Einstellungen gespeichert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const modes = [
    {
      id: 'text',
      title: 'Text-Chat',
      description: 'Klassischer Chat mit Texteingabe',
      icon: MessageSquare,
      color: 'blue',
      status: 'Aktiv & Bereit'
    },
    {
      id: 'voice',
      title: 'Voice-Chat',
      description: 'Sprachnachrichten mit KI-Antworten',
      icon: Mic,
      color: 'purple',
      status: 'Aktiv & Bereit'
    }
    // TWILIO TELEFON-MODUS DEAKTIVIERT - Bei Bedarf wieder aktivieren:
    // {
    //   id: 'phone',
    //   title: 'Echtzeit-Telefon',
    //   description: 'Live-Telefongespräch mit KI',
    //   icon: Phone,
    //   color: 'green',
    //   status: settings?.phone_settings?.twilio_phone_number ? 'Konfiguriert' : 'Twilio-Zugangsdaten erforderlich'
    // }
  ];

  return (
    <div className="space-y-6" data-testid="admin-chat-mode">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Chat-Modus Einstellungen</h1>
        <Settings className="h-6 w-6 text-gray-400" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          {success}
        </div>
      )}

      {/* Mode Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Aktiver Chat-Modus</h2>
        <p className="text-gray-600 mb-6">Wähle den Chat-Modus, der für Bewerber aktiv sein soll.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {modes.map((mode) => {
            const Icon = mode.icon;
            const isActive = settings?.active_mode === mode.id;
            const isDisabled = mode.id === 'phone' && !settings?.phone_settings?.twilio_phone_number;
            
            return (
              <button
                key={mode.id}
                onClick={() => !isDisabled && handleModeChange(mode.id)}
                disabled={saving || isDisabled}
                className={`relative p-6 rounded-xl border-2 transition-all text-left ${
                  isActive
                    ? `border-${mode.color}-500 bg-${mode.color}-50 ring-2 ring-${mode.color}-200`
                    : isDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                style={{
                  borderColor: isActive ? (mode.color === 'blue' ? '#3b82f6' : mode.color === 'purple' ? '#8b5cf6' : '#22c55e') : undefined,
                  backgroundColor: isActive ? (mode.color === 'blue' ? '#eff6ff' : mode.color === 'purple' ? '#f5f3ff' : '#f0fdf4') : undefined
                }}
                data-testid={`chat-mode-${mode.id}`}
              >
                {isActive && (
                  <div className="absolute top-3 right-3">
                    <Check className={`h-5 w-5 ${
                      mode.color === 'blue' ? 'text-blue-500' : 
                      mode.color === 'purple' ? 'text-purple-500' : 'text-green-500'
                    }`} />
                  </div>
                )}
                
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  isActive
                    ? mode.color === 'blue' ? 'bg-blue-100' : mode.color === 'purple' ? 'bg-purple-100' : 'bg-green-100'
                    : 'bg-gray-100'
                }`}>
                  <Icon className={`h-6 w-6 ${
                    isActive
                      ? mode.color === 'blue' ? 'text-blue-600' : mode.color === 'purple' ? 'text-purple-600' : 'text-green-600'
                      : 'text-gray-500'
                  }`} />
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-1">{mode.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{mode.description}</p>
                
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  mode.id === 'phone' && !settings?.phone_settings?.twilio_phone_number
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {mode.status}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bewerber-Auswahl */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <PenLine className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bewerber-Auswahl</h2>
            <p className="text-sm text-gray-600">Bewerber wählt selbst: Schreiben oder Sprechen</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Auswahl-Buttons aktivieren</h4>
              <p className="text-sm text-gray-600">Bewerber sieht beim Start zwei Buttons: "Schreiben" und "Sprechen"</p>
            </div>
            <button
              onClick={() => handleModeChoiceToggle(!settings?.show_mode_choice)}
              className={`p-2 rounded-lg transition-colors ${
                settings?.show_mode_choice 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'bg-gray-200 text-gray-500'
              }`}
              data-testid="toggle-mode-choice"
            >
              {settings?.show_mode_choice ? (
                <ToggleRight className="h-8 w-8" />
              ) : (
                <ToggleLeft className="h-8 w-8" />
              )}
            </button>
          </div>
          
          {/* Begrüßungs-Nachricht */}
          {settings?.show_mode_choice && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Begrüßungs-Nachricht mit Auswahl
              </label>
              <textarea
                value={settings?.mode_choice_message || ''}
                onChange={(e) => handleModeChoiceMessageChange(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Hey 👋 ich bin Lea..."
                data-testid="mode-choice-message"
              />
              <p className="text-xs text-gray-500 mt-1">
                Diese Nachricht erscheint als erstes mit den Auswahl-Buttons darunter
              </p>
            </div>
          )}
          
          {/* Preview */}
          {settings?.show_mode_choice && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
              <h4 className="text-sm font-medium text-indigo-700 mb-3">Vorschau für Bewerber:</h4>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-gray-700 whitespace-pre-line text-sm mb-4">
                  {settings?.mode_choice_message || 'Keine Nachricht'}
                </p>
                <div className="flex gap-2">
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                    ✍️ Schreiben
                  </span>
                  <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium">
                    🎤 Sprechen
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Termin-Modus */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <Settings className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Termin-Modus</h2>
            <p className="text-sm text-gray-600">Mit Terminbuchung oder nur Kontaktformular</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              setSettings(prev => ({ ...prev, appointment_mode: 'with_appointment' }));
              handleSaveAppointmentMode('with_appointment');
            }}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              settings?.appointment_mode !== 'contact_only'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h3 className="font-semibold text-gray-900 mb-1">📅 Mit Termin</h3>
            <p className="text-sm text-gray-600">Chat + Terminbuchung (Standard)</p>
          </button>
          
          <button
            onClick={() => {
              setSettings(prev => ({ ...prev, appointment_mode: 'contact_only' }));
              handleSaveAppointmentMode('contact_only');
            }}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              settings?.appointment_mode === 'contact_only'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h3 className="font-semibold text-gray-900 mb-1">📝 Nur Kontakt</h3>
            <p className="text-sm text-gray-600">Chat + nur Kontaktformular (kein Termin)</p>
          </button>
        </div>
      </div>

      {/* Voice Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Volume2 className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Voice-Chat Einstellungen</h2>
            <p className="text-sm text-gray-600">Konfiguriere die KI-Stimme für Voice-Chat</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              KI-Stimme
            </label>
            <select
              value={settings?.voice_settings?.tts_voice || 'nova'}
              onChange={(e) => handleVoiceSettingsChange('tts_voice', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="voice-select"
            >
              {VOICE_OPTIONS.map((voice) => (
                <option key={voice.value} value={voice.value}>
                  {voice.label} - {voice.description}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sprechgeschwindigkeit: {settings?.voice_settings?.tts_speed || 1.0}x
            </label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={settings?.voice_settings?.tts_speed || 1.0}
              onChange={(e) => handleVoiceSettingsChange('tts_speed', parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
              data-testid="voice-speed"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Langsam (0.5x)</span>
              <span>Normal (1x)</span>
              <span>Schnell (2x)</span>
            </div>
          </div>
        </div>
      </div>

      {/* TWILIO TELEFON-EINSTELLUNGEN DEAKTIVIERT - Bei Bedarf wieder aktivieren
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <Phone className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Telefon Einstellungen</h2>
            <p className="text-sm text-gray-600">Konfiguriere Twilio für Echtzeit-Telefongespräche</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Twilio Telefonnummer
            </label>
            <input
              type="text"
              value={settings?.phone_settings?.twilio_phone_number || ''}
              onChange={(e) => handlePhoneSettingsChange('twilio_phone_number', e.target.value)}
              placeholder="+49 123 456789"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              data-testid="twilio-phone"
            />
            <p className="text-xs text-gray-500 mt-1">
              Die Twilio-Telefonnummer, die Bewerber anrufen können
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Begrüßungsnachricht
            </label>
            <textarea
              value={settings?.phone_settings?.greeting_message || ''}
              onChange={(e) => handlePhoneSettingsChange('greeting_message', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Willkommen bei Factory-Hype! Wie kann ich dir helfen?"
              data-testid="greeting-message"
            />
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 mb-2">Twilio Einrichtung</h4>
            <p className="text-sm text-yellow-700 mb-3">
              Um Telefongespräche zu aktivieren, werden Twilio-Zugangsdaten benötigt:
            </p>
            <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
              <li>Twilio Account SID</li>
              <li>Twilio Auth Token</li>
              <li>Eine Twilio Telefonnummer</li>
            </ul>
            <p className="text-sm text-yellow-700 mt-3">
              Bitte teile diese Zugangsdaten mit, um das Telefon-Feature zu aktivieren.
            </p>
          </div>
        </div>
      </div>
      */}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="save-settings"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Einstellungen speichern
            </>
          )}
        </button>
      </div>
    </div>
  );
}
